import { useState, useEffect, useMemo, useContext } from 'react'
import moment from 'moment'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import Select from 'react-select'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  formatCurrency,
  formatNumber,
  formatDateTime,
  getTimezoneDate,
  capitalizeString,
  formatString,
  createWorkbook,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  addWorksheet,
  DEFAULTNUMHEADERS,
  currencyFormat,
  numberFormat,
  getEventNameForOrder,
  getEventDateForOrder,
  EMPTY_DISPLAY_VALUE,
  checkPermission,
  getDefaultSeason,
  formatSeatsForOrderListing,
  getOrderRefundStatusLabel,
  getOrderRefundAmount,
  getOrderLastRefundDate,
} from '../../utilities/helpers'

import {
  getSeasons,
  getPaymentListingsV4,
  getOrgEventsForReport,
  listOrderListingsReportSubscriptions,
  saveOrderListingsReportSubscription,
  deleteOrderListingsReportSubscription,
  sendOrderListingsReportTestEmail,
} from '../../utilities/api'
import SavedReportScheduleFields, {
  DEFAULT_SAVED_REPORT_SCHEDULE,
} from './SavedReportScheduleFields'
import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'
import { DateRangeInputWrapper } from '../DateInputWrapper'
import {
  dateTime,
  constants,
  ORGANIZATION_PERMISSION_SLUGS,
  getPaymentListingAdditionalFees,
  getPaymentListingDisplayServiceFee,
  getPaymentListingFacilityFee,
  getPaymentListingNet,
  getPaymentListingReportChargebackProtection,
  getPaymentListingReportGross,
  getPaymentListingReportNet,
  getPaymentListingReportProcessingFee,
} from '@blocktickets/shared-utils'

import { ReportActions } from '../ReportActions'
import { ReportRefreshButton } from '../ReportRefreshButton'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { useModal } from '../../providers/ModalProvider/ModalProvider'
import OrderDetailsModal from './OrderDetailsModal'
import {
  expandOrderListingsWithRefundRows,
  isOrderListingRefundRow,
} from './expandOrderListingsWithRefundRows'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import './paymentListingsV4.scss'

const {
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  PAYMENT_TYPE,
  PAYMENT_TYPE_LABEL,
  ORDER_SOURCE,
  ORDER_SOURCE_LABEL,
  PRODUCT_TYPE,
  PRODUCT_TYPE_LABEL,
} = constants

const REMOVED_ORDER_STATUS = [
  ORDER_STATUS.CASH,
  ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT,
  ORDER_STATUS.COMPLETE_FROM_EXCHANGE,
  ORDER_STATUS.COMPLETE_FROM_GROUP_PURCHASE,
  ORDER_STATUS.COMPLETE_FROM_INVOICE,
  ORDER_STATUS.COMPLETE_FROM_TRANSFER,
  ORDER_STATUS.DRAFT,
  ORDER_STATUS.INCOMPLETE,
  ORDER_STATUS.INVOICE_SENT,
  ORDER_STATUS.PAID,
  ORDER_STATUS.PENDING,
  ORDER_STATUS.PENDING_SEND,
  ORDER_STATUS.FAILED,
  ORDER_STATUS.DUPLICATE_ORDER,
  ORDER_STATUS.DUPLICATE,
  ORDER_STATUS.RELEASED,
  ORDER_STATUS.RESERVED,
]

export const ORDER_STATUS_OPTIONS = Object.values(ORDER_STATUS)
  .filter((status) => !REMOVED_ORDER_STATUS.includes(status))
  .map((status) => ({
    value: status,
    label:
      ORDER_STATUS_LABEL[status] || capitalizeString(status.replace(/_/g, ' ')),
  }))

const DEFAULT_ORDER_STATUS_VALUES = [ORDER_STATUS.COMPLETE, ORDER_STATUS.PLAN]

const DEFAULT_SELECTED_STATUSES = ORDER_STATUS_OPTIONS.filter((option) =>
  DEFAULT_ORDER_STATUS_VALUES.includes(option.value)
)

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  ...Object.values(ORDER_SOURCE).map((source) => ({
    value: source,
    label: ORDER_SOURCE_LABEL[source] || capitalizeString(source),
  })),
]

const PAYMENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  ...Object.values(PAYMENT_TYPE).map((type) => ({
    value: type,
    label: PAYMENT_TYPE_LABEL[type] || capitalizeString(type),
  })),
]

const RESALE_PRODUCT_TYPE = 'resale'

const PRODUCT_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  ...[
    ...Object.values(PRODUCT_TYPE)
      .filter((type) => type !== PRODUCT_TYPE.DYNAMIC_PRICING)
      .map((type) => ({
        value: type,
        label: PRODUCT_TYPE_LABEL[type] || capitalizeString(type),
      })),
    { value: RESALE_PRODUCT_TYPE, label: 'Resale' },
  ].sort((a, b) => a.label.localeCompare(b.label)),
]

// Discount type and apply-to labels (promo_code enums from backend)
const DISCOUNT_TYPE_LABEL = {
  decrease_by_price: '$ Decrease',
  decrease_by_percent: '% Decrease',
}
const APPLY_DISCOUNT_TO_LABEL = {
  total_price: 'Total Price',
  individual_tickets: 'Per Ticket',
}

export const PAYMENT_LISTINGS_V4_ALL_COLUMNS = [
  { id: 'createdAt', label: 'Order date', isNumeric: false },
  { id: 'orderId', label: 'Order id', isNumeric: false },
  { id: 'customerEmail', label: 'Customer Email', isNumeric: false },
  { id: 'source', label: 'Source', isNumeric: false },
  { id: 'buyerType', label: 'Buyer type', isNumeric: false },
  { id: 'paymentMethod', label: 'Payment method', isNumeric: false },
  { id: 'net', label: 'Net', isNumeric: true, summable: true },
  { id: 'status', label: 'Status', isNumeric: false },
  { id: 'quantity', label: 'Quantity', isNumeric: true },
  { id: 'seats', label: 'Seats', isNumeric: false },
  { id: 'basePrice', label: 'Base Price', isNumeric: true, summable: true },
  { id: 'offerPrice', label: 'Offer Price', isNumeric: true, summable: true },
  { id: 'facilityFee', label: 'Facility fee', isNumeric: true, summable: true },
  { id: 'salesTax', label: 'Sales tax', isNumeric: true, summable: true },
  { id: 'gross', label: 'Gross', isNumeric: true, summable: true },
  { id: 'serviceFee', label: 'Service fee', isNumeric: true, summable: true },
  {
    id: 'processingFee',
    label: 'Processing fee',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'chargebackProtection',
    label: 'Chargeback protection',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'totalFeeAmount',
    label: 'Additional Fees',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'amountRemaining',
    label: 'Amount Remaining',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'redemptionValue',
    label: 'Redemption Value',
    isNumeric: true,
    summable: true,
  },
  { id: 'discountCode', label: 'Discount Code', isNumeric: false },
  { id: 'discountType', label: 'Discount Type', isNumeric: false },
  { id: 'applyDiscountTo', label: 'Apply Discount To', isNumeric: false },
  { id: 'discountAmount', label: 'Discount Amount', isNumeric: false },
  {
    id: 'totalDiscount',
    label: 'Total Discount',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'refundStatus',
    label: 'Refund status',
    isNumeric: false,
  },
  {
    id: 'refundAmount',
    label: 'Refund amount',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'lastRefundAt',
    label: 'Last refund date',
    isNumeric: false,
  },
  { id: 'productType', label: 'Product type', isNumeric: false },
  {
    id: 'resaleAskingPrice',
    label: 'Asking price',
    isNumeric: true,
  },
  {
    id: 'resaleSellerPayout',
    label: 'Seller payout',
    isNumeric: true,
    summable: true,
  },
  {
    id: 'resalePlatformShare',
    label: 'Platform share',
    isNumeric: true,
    summable: true,
  },
  { id: 'paymentPlan', label: 'Payment Plan', isNumeric: false },
  { id: 'paymentType', label: 'Payment type', isNumeric: false },
  { id: 'accountRep', label: 'Account Rep', isNumeric: false },
  { id: 'invoiceNumber', label: 'Invoice #', isNumeric: false },
  { id: 'dateOfIssue', label: 'Date of Issue', isNumeric: false },
  { id: 'dueDate', label: 'Due Date', isNumeric: false },
  { id: 'datePaid', label: 'Date Paid', isNumeric: false },
  { id: 'agingBucket', label: 'Aging Bucket', isNumeric: false },
  { id: 'organizationName', label: 'Organization Name', isNumeric: false },
  { id: 'last4', label: 'Last4', isNumeric: false },
  { id: 'customerName', label: 'Customer name', isNumeric: false },
  { id: 'eventName', label: 'Event', isNumeric: false },
  { id: 'eventStart', label: 'Event date and time', isNumeric: false },
]

/** Default-visible Order Listings columns (primary view). */
export const ORDER_LISTINGS_DEFAULT_VISIBLE_IDS = new Set([
  'createdAt',
  'orderId',
  'customerEmail',
  'source',
  'buyerType',
  'paymentMethod',
  'net',
  'status',
])

// Bumped so returning users pick up the latest default column set
const PAYMENT_LISTINGS_V4_LOCAL_STORAGE_KEY = 'paymentListingsV4Columns_v6'
const SEASON_REPORT_LOCAL_STORAGE_KEY = 'seasonReportColumns_v2'
const SEASON_REPORT_COLUMN = { id: 'season', label: 'Season', isNumeric: false }
const RENEWAL_REPORT_COLUMN = {
  id: 'renewal',
  label: 'Renewal',
  isNumeric: false,
}
const PRICING_TIER_REPORT_COLUMN = {
  id: 'pricingTier',
  label: 'Pricing Tier',
  isNumeric: false,
}
const PRICING_TIER_PRICE_REPORT_COLUMN = {
  id: 'pricingTierPrice',
  label: 'Pricing Tier Price',
  isNumeric: false,
}
const SEASON_TICKET_BASE_REVENUE_REPORT_COLUMN = {
  id: 'seasonTicketBaseRevenue',
  label: 'Season Ticket Base Revenue',
  isNumeric: true,
  summable: true,
}
const SHARED_ENRICHED_ORDER_COLUMNS = [
  SEASON_REPORT_COLUMN,
  RENEWAL_REPORT_COLUMN,
  PRICING_TIER_REPORT_COLUMN,
  PRICING_TIER_PRICE_REPORT_COLUMN,
]
const getAllColumns = (isSeasonReport = false) =>
  isSeasonReport
    ? [
        ...SHARED_ENRICHED_ORDER_COLUMNS,
        SEASON_TICKET_BASE_REVENUE_REPORT_COLUMN,
        ...PAYMENT_LISTINGS_V4_ALL_COLUMNS,
      ]
    : [...SHARED_ENRICHED_ORDER_COLUMNS, ...PAYMENT_LISTINGS_V4_ALL_COLUMNS]
const humanizeHeader = (str = '') =>
  str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()

const formatPaymentMethodLabel = (paymentMethod) =>
  paymentMethod ? formatString(paymentMethod) : '—'

const formatListingRefundStatus = (listing) =>
  getOrderRefundStatusLabel(listing) || EMPTY_DISPLAY_VALUE

const getNetValueForListing = (listing) => getPaymentListingReportNet(listing)

const getProcessingFeeValueForListing = (listing) =>
  getPaymentListingReportProcessingFee(listing)

const getGrossValueForListing = (listing) =>
  getPaymentListingReportGross(listing)

const getServiceFeeValueForListing = (listing) =>
  getPaymentListingDisplayServiceFee(listing)

const getChargebackValueForListing = (listing) =>
  getPaymentListingReportChargebackProtection(listing)

const getAdditionalFeesValueForListing = (listing) =>
  getPaymentListingAdditionalFees(listing)

const getFacilityFeeValueForListing = (listing) =>
  getPaymentListingFacilityFee(listing)

const toNegativeFeeDisplay = (value) => (value ? -Math.abs(value) : 0)
/** Fees display as negatives on sales; refund lines reverse that to positives. */
const toListingFeeDisplay = (listing, value) =>
  isOrderListingRefundRow(listing)
    ? value
      ? Math.abs(value)
      : 0
    : toNegativeFeeDisplay(value)
const toListingNumber = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}
const formatListingNumericCurrency = (value) =>
  formatCurrency(toListingNumber(value))
const formatListingNumericCount = (value) =>
  formatNumber(toListingNumber(value))
const formatRenewalForDisplay = (listing) =>
  listing?.packageRenewal === true ? 'Yes' : 'No'
export const getListingRowClassName = (listing) => {
  const classes = ['md-x', 'md-x--scroll']
  if (isResaleListing(listing)) classes.push('order-listing-resale-row')
  if (isOrderListingRefundRow(listing)) classes.push('order-listing-refund-row')
  return classes.join(' ')
}

const isComplimentaryOrderListing = (listing) =>
  listing?.complimentary === true ||
  listing?.complimentary === 't' ||
  String(listing?.paymentMethod || '').toLowerCase() === 'complimentary'

const roundListingMoney = (n) => Math.round(n * 100) / 100

const seasonTicketBaseRevenueFromCount = (listing, packageTicketQty) => {
  if (listing?.productType !== PRODUCT_TYPE.SEASON_PACKAGES) return null
  if (isComplimentaryOrderListing(listing)) return 0

  const tierPrice = Number(listing?.pricingTierPrice)
  const qty = Number(packageTicketQty)
  if (!Number.isFinite(tierPrice) || !Number.isFinite(qty)) return null
  return tierPrice * qty
}

const getSeasonTicketBaseRevenue = (listing) => {
  if (isOrderListingRefundRow(listing)) {
    const original = seasonTicketBaseRevenueFromCount(
      listing,
      listing?.packageTicketsCountBeforeRefund
    )
    if (original == null) return null
    const scale = Number(listing?.orderListingRefundScale)
    if (!Number.isFinite(scale) || scale <= 0) return 0
    return roundListingMoney(-Math.abs(original) * scale)
  }

  return seasonTicketBaseRevenueFromCount(listing, listing?.packageTicketsCount)
}

const formatDiscountAmountDisplay = (listing) => {
  const val = listing?.discountAmountValue
  if (val == null || val === '') return EMPTY_DISPLAY_VALUE
  const isPercent =
    listing?.discountAmountIsPercent === true ||
    listing?.discountAmountIsPercent === 't'
  if (isPercent) return `${Number(val)}%`
  return formatCurrency(Number(val))
}

const RESALE_DETAIL_COLUMN_IDS = new Set([
  'resaleAskingPrice',
  'resaleSellerPayout',
  'resalePlatformShare',
])

const isResaleListing = (listing) =>
  String(listing?.type || listing?.productType || '')
    .trim()
    .toLowerCase() === RESALE_PRODUCT_TYPE

const formatResaleDetailValue = (listing, field) => {
  if (!isResaleListing(listing)) return EMPTY_DISPLAY_VALUE
  if (field === 'resaleAskingPrice' && isOrderListingRefundRow(listing)) {
    return EMPTY_DISPLAY_VALUE
  }
  const amount = listing?.[field]
  if (amount === null || amount === undefined || amount === '') {
    return EMPTY_DISPLAY_VALUE
  }
  return formatListingNumericCurrency(amount)
}

const getDefaultColumns = (isSeasonReport = false) =>
  getAllColumns(isSeasonReport).map((col) => ({
    ...col,
    visible: RESALE_DETAIL_COLUMN_IDS.has(col.id)
      ? false
      : isSeasonReport
        ? true
        : ORDER_LISTINGS_DEFAULT_VISIBLE_IDS.has(col.id),
  }))

export const serializeColumnsForTemplate = (columns = []) =>
  columns.map((col) => ({
    id: col.id,
    visible: Boolean(col.visible),
  }))

export const mergeSavedColumnsWithDefaults = (
  savedColumns = [],
  isSeasonReport = false
) => {
  const defaultColumns = getDefaultColumns(isSeasonReport)
  const defaultById = new Map(defaultColumns.map((col) => [col.id, col]))
  const seen = new Set()
  const merged = []

  // Keep saved order; append any newly added default columns at the end
  for (const saved of savedColumns) {
    const base = defaultById.get(saved.id)
    if (!base || seen.has(saved.id)) continue
    seen.add(saved.id)
    merged.push({ ...base, visible: Boolean(saved.visible) })
  }

  for (const defaultCol of defaultColumns) {
    if (!seen.has(defaultCol.id)) {
      merged.push(defaultCol)
    }
  }

  return merged
}

const DEFAULT_SAVED_REPORT_VALUE = 'default'

const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 7h-9"></path>
    <path d="M14 17H5"></path>
    <circle cx="17" cy="17" r="3"></circle>
    <circle cx="7" cy="7" r="3"></circle>
  </svg>
)

const ExcelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
)

const DragHandleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 25 25"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.5 8C10.3284 8 11 7.32843 11 6.5C11 5.67157 10.3284 5 9.5 5C8.67157 5 8 5.67157 8 6.5C8 7.32843 8.67157 8 9.5 8ZM9.5 14C10.3284 14 11 13.3284 11 12.5C11 11.6716 10.3284 11 9.5 11C8.67157 11 8 11.6716 8 12.5C8 13.3284 8.67157 14 9.5 14ZM11 18.5C11 19.3284 10.3284 20 9.5 20C8.67157 20 8 19.3284 8 18.5C8 17.6716 8.67157 17 9.5 17C10.3284 17 11 17.6716 11 18.5ZM15.5 8C16.3284 8 17 7.32843 17 6.5C17 5.67157 16.3284 5 15.5 5C14.6716 5 14 5.67157 14 6.5C14 7.32843 14.6716 8 15.5 8ZM17 12.5C17 13.3284 16.3284 14 15.5 14C14.6716 14 14 13.3284 14 12.5C14 11.6716 14.6716 11 15.5 11C16.3284 11 17 11.6716 17 12.5ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z"
      fill="#777e90"
    />
  </svg>
)

const ResetIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
)

const SortableColumnItem = ({ col, index, toggleColumn }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`column-settings-item ${isDragging ? 'is-dragging' : ''}`}
    >
      <Form.Check
        type="switch"
        id={`col-${col.id}`}
        checked={col.visible}
        onChange={() => toggleColumn(index)}
      />
      <div className="column-label">{col.label}</div>
      <div {...attributes} {...listeners} className="drag-handle">
        <DragHandleIcon />
      </div>
    </div>
  )
}

export default function PaymentListingsV4Wrapper({
  organization,
  isSeasonReport = false,
}) {
  const { openModal } = useModal()
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDER_LISTINGS
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [paymentListings, setPaymentListings] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [events, setEvents] = useState([])
  const [selectedEventUuid, setSelectedEventUuid] = useState('all')
  const [selectedStatuses, setSelectedStatuses] = useState(
    DEFAULT_SELECTED_STATUSES
  )
  const [selectedSource, setSelectedSource] = useState('all')
  const [selectedPaymentType, setSelectedPaymentType] = useState('all')
  const [selectedProductType, setSelectedProductType] = useState('all')
  const [dateFilterType, setDateFilterType] = useState('order_date') // "order_date" | "date_paid"
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const selectedSeasonLabel = useMemo(() => {
    if (!isSeasonReport) return ''
    return (
      seasons.find((season) => String(season.id) === String(selectedSeason))
        ?.name || ''
    )
  }, [isSeasonReport, seasons, selectedSeason])
  const localStorageKey = isSeasonReport
    ? SEASON_REPORT_LOCAL_STORAGE_KEY
    : PAYMENT_LISTINGS_V4_LOCAL_STORAGE_KEY

  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem(localStorageKey)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) {
          return mergeSavedColumnsWithDefaults(parsed, isSeasonReport)
        }
      } catch (e) {
        console.error('Error parsing saved columns', e)
      }
    }
    return getDefaultColumns(isSeasonReport)
  })

  const [showSettings, setShowSettings] = useState(false)
  const [savedReports, setSavedReports] = useState([])
  const [selectedReportId, setSelectedReportId] = useState(
    DEFAULT_SAVED_REPORT_VALUE
  )
  const [reportName, setReportName] = useState('')
  const [reportSchedule, setReportSchedule] = useState({
    ...DEFAULT_SAVED_REPORT_SCHEDULE,
    email: AuthService.getUser()?.user?.email || '',
  })
  const [reportError, setReportError] = useState('')
  const [reportMessage, setReportMessage] = useState('')
  const [isSavingReport, setIsSavingReport] = useState(false)
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(columns))
  }, [columns, localStorageKey])

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible),
    [columns]
  )

  const enrichedListings = useMemo(
    () => expandOrderListingsWithRefundRows(paymentListings || []),
    [paymentListings]
  )

  const selectedReport = useMemo(
    () =>
      savedReports.find(
        (report) => String(report.id) === String(selectedReportId)
      ) || null,
    [savedReports, selectedReportId]
  )

  const currentReportFilters = useMemo(
    () => ({
      paymentType: selectedPaymentType,
      source: selectedSource,
      productType: selectedProductType,
      statuses: selectedStatuses.map((status) => status.value),
      dateField: dateFilterType,
      eventUuid: selectedEventUuid,
    }),
    [
      selectedPaymentType,
      selectedSource,
      selectedProductType,
      selectedStatuses,
      dateFilterType,
      selectedEventUuid,
    ]
  )

  useEffect(() => {
    const loadEventsAndSeasons = async () => {
      if (!organization?.uuid) return
      try {
        const requests = [getOrgEventsForReport(organization.uuid)]
        if (isSeasonReport) {
          requests.push(getSeasons(organization.uuid))
        }
        const [eventsRes, seasonsRes] = await Promise.all(requests)
        const loadedEvents = [...(eventsRes.data || [])].sort((a, b) => {
          const startA = a?.start ? new Date(a.start).getTime() : 0
          const startB = b?.start ? new Date(b.start).getTime() : 0
          return startB - startA
        })
        setEvents(loadedEvents)
        if (isSeasonReport) {
          const loadedSeasons = seasonsRes?.data || []
          setSeasons(loadedSeasons)
          const defaultSeason = getDefaultSeason(loadedSeasons)
          setSelectedSeason(
            String(defaultSeason?.id || loadedSeasons?.[0]?.id || '')
          )
        }
      } catch (error) {
        console.error('Error fetching events or seasons:', error)
      } finally {
        hideLoading()
      }
    }
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    showLoading()
    loadEventsAndSeasons()
  }, [organization?.uuid, orgPermissionsReady, hasPermission, isSeasonReport])

  useEffect(() => {
    if (isSeasonReport) return
    if (!orgPermissionsReady || !hasPermission) return
    if (!organization?.uuid) return

    const loadSavedReports = async () => {
      try {
        const res = await listOrderListingsReportSubscriptions(
          organization.uuid
        )
        const reports = Array.isArray(res?.data?.subscriptions)
          ? res.data.subscriptions
          : []
        setSavedReports(reports)
      } catch (error) {
        console.error('Error fetching saved Order Listings reports:', error)
      }
    }

    loadSavedReports()
  }, [isSeasonReport, orgPermissionsReady, hasPermission, organization?.uuid])

  useEffect(() => {
    if (
      selectedProductType === PRODUCT_TYPE.FLEX_PACKAGES ||
      selectedProductType === PRODUCT_TYPE.SEASON_PACKAGES
    ) {
      setSelectedEventUuid('all')
    }
  }, [selectedProductType])

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || !toDate) return
      if (isSeasonReport && !selectedSeason) return

      setIsLoadingData(true)

      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        const pad = (n) => String(n).padStart(2, '0')
        const ymd = (d) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

        const startLocalIso = `${ymd(fromDate)}T00:00:00.000`
        const endLocalIso = `${ymd(toDate)}T23:59:59.999`

        const fromUtcIso = dateTime.localToUtc(startLocalIso, tz).toISOString()
        const toUtcIso = dateTime.localToUtc(endLocalIso, tz).toISOString()

        const datePaidOpts =
          dateFilterType === 'date_paid'
            ? {
                dateField: dateFilterType,
                datePaidFrom: ymd(fromDate),
                datePaidTo: ymd(toDate),
              }
            : { dateField: dateFilterType }

        const res = await getPaymentListingsV4(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          selectedEventUuid,
          selectedStatuses.map((s) => s.value),
          selectedSource,
          selectedPaymentType,
          selectedProductType,
          'all',
          undefined,
          datePaidOpts,
          isSeasonReport ? selectedSeason : undefined
        )

        setPaymentListings(res.data || [])
      } catch (error) {
        console.error('Error fetching order listings v4:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady || !hasPermission) return
    if (!organization?.uuid) return
    fetchData()
  }, [
    fromDate,
    toDate,
    dateFilterType,
    organization.uuid,
    selectedEventUuid,
    selectedStatuses,
    selectedSource,
    selectedPaymentType,
    selectedProductType,
    selectedSeason,
    isSeasonReport,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id)
        const newIndex = items.findIndex((i) => i.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (index) => {
    const newColumns = [...columns]
    newColumns[index].visible = !newColumns[index].visible
    setColumns(newColumns)
  }

  const resetToDefaultReport = () => {
    setColumns(getDefaultColumns(isSeasonReport))
    setSelectedReportId(DEFAULT_SAVED_REPORT_VALUE)
    setReportName('')
    setReportSchedule({
      ...DEFAULT_SAVED_REPORT_SCHEDULE,
      email: AuthService.getUser()?.user?.email || '',
    })
    setReportError('')
    setReportMessage('')
    setSelectedPaymentType('all')
    setSelectedSource('all')
    setSelectedProductType('all')
    setSelectedStatuses(DEFAULT_SELECTED_STATUSES)
    setDateFilterType('order_date')
    setSelectedEventUuid('all')
  }

  const resetColumns = () => {
    setColumns(getDefaultColumns(isSeasonReport))
  }

  const applySavedReport = (reportId) => {
    if (!reportId || reportId === DEFAULT_SAVED_REPORT_VALUE) {
      resetToDefaultReport()
      return
    }

    const report = savedReports.find(
      (item) => String(item.id) === String(reportId)
    )
    if (!report) {
      resetToDefaultReport()
      return
    }

    const filters = report.filters || {}
    setSelectedReportId(report.id)
    setReportName(report.name || '')
    setReportSchedule({
      ...DEFAULT_SAVED_REPORT_SCHEDULE,
      ...(report.schedule || {}),
      email: report.schedule?.email || AuthService.getUser()?.user?.email || '',
    })
    setReportError('')
    setReportMessage('')
    setColumns(
      mergeSavedColumnsWithDefaults(report.columns || [], isSeasonReport)
    )
    setSelectedPaymentType(filters.paymentType || 'all')
    setSelectedSource(filters.source || 'all')
    setSelectedProductType(filters.productType || 'all')
    setDateFilterType(filters.dateField || 'order_date')
    setSelectedEventUuid(filters.eventUuid || 'all')
    setSelectedStatuses(
      (filters.statuses || [])
        .map((status) =>
          ORDER_STATUS_OPTIONS.find((option) => option.value === status)
        )
        .filter(Boolean)
    )
  }

  const buildReportPayload = ({ updateExisting = false } = {}) => ({
    id: updateExisting && selectedReport ? selectedReport.id : undefined,
    organizationUUID: organization.uuid,
    name: reportName.trim(),
    filters: currentReportFilters,
    columns: serializeColumnsForTemplate(columns),
    schedule: reportSchedule,
    exportFormat: 'xlsx',
  })

  const handleSaveReport = async ({ updateExisting = false } = {}) => {
    const name = reportName.trim()
    if (!name) {
      setReportError('Enter a name for this saved report.')
      return
    }
    if (reportSchedule.enabled && !String(reportSchedule.email || '').trim()) {
      setReportError('Enter an email address for scheduled delivery.')
      return
    }

    setIsSavingReport(true)
    setReportError('')
    setReportMessage('')

    try {
      const res = await saveOrderListingsReportSubscription(
        buildReportPayload({ updateExisting })
      )
      const saved = res?.data
      const listRes = await listOrderListingsReportSubscriptions(
        organization.uuid
      )
      const reports = Array.isArray(listRes?.data?.subscriptions)
        ? listRes.data.subscriptions
        : []
      setSavedReports(reports)
      const active =
        reports.find((report) => String(report.id) === String(saved?.id)) ||
        saved
      if (active?.id) {
        setSelectedReportId(active.id)
        setReportName(active.name || name)
        setReportSchedule({
          ...DEFAULT_SAVED_REPORT_SCHEDULE,
          ...(active.schedule || reportSchedule),
        })
      }
      setReportMessage(
        reportSchedule.enabled
          ? 'Report saved with email schedule.'
          : 'Report saved.'
      )
    } catch (error) {
      console.error('Error saving Order Listings report:', error)
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not save this report. Please try again.'
      )
    } finally {
      setIsSavingReport(false)
    }
  }

  const handleDeleteReport = async () => {
    if (!selectedReport) return

    setIsSavingReport(true)
    setReportError('')
    setReportMessage('')

    try {
      await deleteOrderListingsReportSubscription(
        selectedReport.id,
        organization.uuid
      )
      setSavedReports((prev) =>
        prev.filter((report) => String(report.id) !== String(selectedReport.id))
      )
      resetToDefaultReport()
      setReportMessage('Saved report deleted.')
    } catch (error) {
      console.error('Error deleting Order Listings report:', error)
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not delete this report. Please try again.'
      )
    } finally {
      setIsSavingReport(false)
    }
  }

  const handleSendTestReportEmail = async () => {
    const name = reportName.trim() || selectedReport?.name || 'Order Listings'
    if (reportSchedule.enabled && !String(reportSchedule.email || '').trim()) {
      setReportError('Enter an email address before sending a test.')
      return
    }

    setIsSendingTestEmail(true)
    setReportError('')
    setReportMessage('')

    try {
      const res = await sendOrderListingsReportTestEmail({
        organizationUUID: organization.uuid,
        name,
        filters: currentReportFilters,
        columns: serializeColumnsForTemplate(columns),
        schedule: {
          ...reportSchedule,
          enabled: true,
          email:
            reportSchedule.email || AuthService.getUser()?.user?.email || '',
        },
        exportFormat: 'xlsx',
      })
      const email = res?.data?.email || reportSchedule.email
      setReportMessage(`Test email sent to ${email}.`)
    } catch (error) {
      console.error('Error sending Order Listings test email:', error)
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not send test email. Please try again.'
      )
    } finally {
      setIsSendingTestEmail(false)
    }
  }

  const downloadReport = async () => {
    try {
      if (!paymentListings || paymentListings.length === 0) return

      const dateRange = `${moment(fromDate).format('MMM D, YYYY')} - ${moment(
        toDate
      ).format('MMM D, YYYY')}`
      const dateFilterLabel =
        dateFilterType === 'date_paid' ? 'Date paid' : 'Order date'

      const workbook = createWorkbook()
      const worksheet = addWorksheet('Order Listings', workbook)

      // meta header rows
      const headerLines = [`Date Range (${dateFilterLabel}): ${dateRange}`]
      setHeaders(
        'Order Listings',
        organization?.name || '',
        worksheet,
        headerLines
      )

      const formatRow = (listing) => {
        const out = {}
        visibleColumns.forEach((col) => {
          let value = listing?.[col.id]
          if (col.id === 'season') {
            value =
              listing?.season || selectedSeasonLabel || EMPTY_DISPLAY_VALUE
          }
          if (col.id === 'renewal') {
            value = formatRenewalForDisplay(listing)
          }
          if (col.id === 'pricingTier') {
            value = listing?.pricingTier || EMPTY_DISPLAY_VALUE
          }
          if (col.id === 'pricingTierPrice') {
            value = formatListingNumericCurrency(listing?.pricingTierPrice)
          }
          if (col.id === 'seasonTicketBaseRevenue') {
            value = toListingNumber(getSeasonTicketBaseRevenue(listing))
          }

          if (col.id === 'createdAt') {
            const zonedDate = getTimezoneDate(
              listing.processedAt ?? listing.createdAt,
              listing.timezone
            )
            value = zonedDate ? formatDateTime(zonedDate) : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'eventStart') {
            value = getEventDateForOrder(listing, listing.timezone)
          }

          if (col.id === 'eventName') {
            value = getEventNameForOrder(listing)
          }

          if (col.id === 'seats') {
            value = formatSeatsForOrderListing(
              listing?.seatTickets,
              EMPTY_DISPLAY_VALUE
            )
          }

          if (col.id === 'productType') {
            value =
              PRODUCT_TYPE_OPTIONS.find(
                (opt) => opt.value === listing.productType
              )?.label ||
              listing.productType ||
              EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'status') {
            const refundStatus = getOrderRefundStatusLabel(listing)
            const baseStatus =
              ORDER_STATUS_LABEL[listing.status] ||
              capitalizeString(
                listing.status?.replace(/_/g, ' ') || EMPTY_DISPLAY_VALUE
              )
            if (isOrderListingRefundRow(listing)) {
              value = refundStatus || baseStatus
            } else {
              value =
                refundStatus === 'Partially Refunded'
                  ? `${baseStatus} (${refundStatus})`
                  : baseStatus
            }
          }

          if (col.id === 'refundStatus') {
            value = formatListingRefundStatus(listing)
          }

          if (col.id === 'refundAmount') {
            value = isOrderListingRefundRow(listing)
              ? 0
              : toListingNumber(getOrderRefundAmount(listing))
          }

          if (col.id === 'lastRefundAt') {
            const lastRefundAt = getOrderLastRefundDate(listing)
            value = lastRefundAt
              ? formatDateTime(moment(lastRefundAt))
              : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'paymentType') {
            value =
              PAYMENT_TYPE_LABEL[listing.paymentType] ||
              capitalizeString(listing.paymentType || EMPTY_DISPLAY_VALUE)
          }
          if (col.id === 'paymentPlan') {
            value = listing?.paymentPlan ? 'True' : 'False'
          }

          if (col.id === 'paymentMethod') {
            value = formatPaymentMethodLabel(listing.paymentMethod)
          }

          if (col.id === 'source') {
            value =
              ORDER_SOURCE_LABEL[listing.source] ||
              capitalizeString(listing.source || EMPTY_DISPLAY_VALUE)
          }

          if (col.id === 'buyerType') {
            value =
              (listing.buyerType && String(listing.buyerType).trim()) ||
              EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'last4') {
            value = listing.last4
              ? listing.last4.toString().padStart(4, '0')
              : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'customerName') {
            value = capitalizeString(
              listing.customerName?.trim() || EMPTY_DISPLAY_VALUE
            )
          }

          if (col.id === 'customerEmail') {
            value = listing.customerEmail?.trim() || EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'accountRep') {
            value = capitalizeString(
              listing.accountRep?.trim() || EMPTY_DISPLAY_VALUE
            )
          }

          if (col.id === 'invoiceNumber') {
            value = listing.invoiceNumber || EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'dateOfIssue') {
            value = listing.dateOfIssue
              ? formatDateTime(moment(listing.dateOfIssue), 'dateOnly')
              : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'dueDate') {
            value = listing.dueDate
              ? formatDateTime(moment(listing.dueDate), 'dateOnly')
              : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'datePaid') {
            value = listing.datePaid
              ? formatDateTime(moment(listing.datePaid), 'dateOnly')
              : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'agingBucket') {
            value =
              listing.dueDate != null && listing.agingBucket
                ? listing.agingBucket
                : EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'organizationName') {
            value = listing.organizationName?.trim() || EMPTY_DISPLAY_VALUE
          }

          if (col.id === 'redemptionValue') {
            value =
              listing.voucher && listing.flex_pack
                ? toListingNumber(listing.redemptionValue)
                : 0
          }
          if (col.id === 'amountRemaining') {
            value = toListingNumber(listing?.amountRemaining)
          }

          if (col.id === 'discountCode') {
            value =
              (listing.discountCode && String(listing.discountCode).trim()) ||
              EMPTY_DISPLAY_VALUE
          }
          if (col.id === 'discountType') {
            value =
              DISCOUNT_TYPE_LABEL[listing.discountType] ||
              listing.discountType ||
              EMPTY_DISPLAY_VALUE
          }
          if (col.id === 'applyDiscountTo') {
            value =
              APPLY_DISCOUNT_TO_LABEL[listing.applyDiscountTo] ||
              listing.applyDiscountTo ||
              EMPTY_DISPLAY_VALUE
          }
          if (col.id === 'discountAmount') {
            value = formatDiscountAmountDisplay(listing)
          }
          if (col.isNumeric) {
            if (col.id === 'serviceFee') {
              value = toListingFeeDisplay(
                listing,
                getServiceFeeValueForListing(listing)
              )
            } else if (col.id === 'facilityFee') {
              value = toListingFeeDisplay(
                listing,
                getFacilityFeeValueForListing(listing)
              )
            } else if (col.id === 'totalFeeAmount') {
              value = toListingFeeDisplay(
                listing,
                getAdditionalFeesValueForListing(listing)
              )
            } else if (col.id === 'chargebackProtection') {
              value = toListingFeeDisplay(
                listing,
                getChargebackValueForListing(listing)
              )
            } else if (col.id === 'processingFee') {
              value = toListingFeeDisplay(
                listing,
                getProcessingFeeValueForListing(listing)
              )
            } else if (col.id === 'gross') {
              value = getGrossValueForListing(listing)
            } else if (col.id === 'net') {
              value = getNetValueForListing(listing) ?? 0
            } else if (RESALE_DETAIL_COLUMN_IDS.has(col.id)) {
              const hideAskingPriceOnRefund =
                col.id === 'resaleAskingPrice' &&
                isOrderListingRefundRow(listing)
              value =
                isResaleListing(listing) && !hideAskingPriceOnRefund
                  ? (listing?.[col.id] ?? '')
                  : ''
            } else if (
              col.id !== 'redemptionValue' &&
              col.id !== 'totalFeeAmount' &&
              col.id !== 'seasonTicketBaseRevenue' &&
              col.id !== 'amountRemaining'
            ) {
              value = listing?.[col.id] ?? 0
            }
          }
          out[col.id] = col.isNumeric
            ? (value ?? 0)
            : (value ?? EMPTY_DISPLAY_VALUE)
        })
        return out
      }

      const keys = visibleColumns.map((c) => c.id)

      setColumnsAndRows(keys, enrichedListings, formatRow, worksheet)

      const headerRows = DEFAULTNUMHEADERS + headerLines.length
      const columnHeaderRowNumber = headerRows

      const headerRow = worksheet.getRow(columnHeaderRowNumber)
      visibleColumns.forEach((col, idx) => {
        headerRow.getCell(idx + 1).value = humanizeHeader(col.label)
      })
      headerRow.commit?.()

      // format numeric columns
      visibleColumns.forEach((col, idx) => {
        const excelColNumber = idx + 1
        if (!col.isNumeric) return

        if (
          [
            'basePrice',
            'offerPrice',
            'facilityFee',
            'salesTax',
            'gross',
            'serviceFee',
            'processingFee',
            'chargebackProtection',
            'totalFeeAmount',
            'net',
            'redemptionValue',
            'totalDiscount',
            'seasonTicketBaseRevenue',
            'refundAmount',
            'amountRemaining',
          ].includes(col.id)
        ) {
          currencyFormat(excelColNumber, worksheet, headerRows)
        } else {
          numberFormat(excelColNumber, worksheet, headerRows)
        }
      })

      await saveWorkbook(workbook, 'order listings')
    } catch (error) {
      console.error('Excel export failed:', error)
    }
  }

  const openOrderDetails = (id, orderId) => {
    openModal(
      OrderDetailsModal,
      {
        id,
        orderId,
        organizationUUID: organization.uuid,
      },
      { className: 'modal-md', scrollable: true }
    )
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className="spacer-sm"
      data-testid="payment-listings-v4-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>{isSeasonReport ? 'Season Report' : 'Order Listings'}</h1>
        </div>
        <p className="section-header-desc">
          {isSeasonReport
            ? 'View season-scoped order listings, including flex packs, season packages, and event orders tied to the selected season.'
            : 'View all order records from Website, Box Office, and Ticket Assignment orders within the selected date range.'}
        </p>
      </header>

      <div className="payment-listings-v4-filters mb-4">
        <Stack gap={3}>
          <Stack direction="horizontal" gap={3} className="align-items-end">
            {isSeasonReport && (
              <Form.Group controlId="seasonSelect" style={{ width: '320px' }}>
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Season
                </Form.Label>
                <Form.Select
                  className="form-control-height"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  disabled={isLoadingData}
                >
                  <option value="" disabled>
                    Select season
                  </option>
                  {seasons.map((season) => (
                    <option key={season.id} value={String(season.id)}>
                      {season.organization?.name
                        ? `${season.organization.name} - `
                        : ''}
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <div style={{ width: '400px' }}>
              <Form.Group controlId="dateRangeGroup">
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Date Range
                </Form.Label>
                <DateRangeInputWrapper
                  id="dateRange"
                  startDate={fromDate}
                  endDate={toDate}
                  onChange={handleDateRangeChange}
                  isReadOnly={isLoadingData}
                  size="sm"
                  styles="form-control-height"
                />
              </Form.Group>
            </div>

            <Form.Group
              controlId="dateFilterTypeSelect"
              style={{ width: '180px' }}
            >
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Filter by
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={dateFilterType}
                onChange={(e) => setDateFilterType(e.target.value)}
                disabled={isLoadingData}
              >
                <option value="order_date">Order date</option>
                <option value="date_paid">Date paid</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="productTypeSelect" style={{ flex: 1 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Product Type
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={selectedProductType}
                onChange={(e) => setSelectedProductType(e.target.value)}
                disabled={isLoadingData}
              >
                {PRODUCT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {!isSeasonReport && (
              <Form.Group controlId="eventSelect" style={{ flex: 2 }}>
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Select Event
                </Form.Label>
                <Form.Select
                  size="sm"
                  className="form-control-height"
                  value={selectedEventUuid}
                  onChange={(e) => setSelectedEventUuid(e.target.value)}
                  disabled={
                    isLoadingData ||
                    selectedProductType === PRODUCT_TYPE.FLEX_PACKAGES ||
                    selectedProductType === PRODUCT_TYPE.SEASON_PACKAGES
                  }
                >
                  <option value="all">All</option>
                  {events.map((event) => (
                    <option key={event.uuid} value={event.uuid}>
                      {event.name}
                      {event.start &&
                        ` - ${moment(event.start).format('MMM D, YYYY')}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group controlId="sourceSelect" style={{ flex: 1 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Source
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                disabled={isLoadingData}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="paymentTypeSelect" style={{ flex: 1 }}>
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Payment Type
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={selectedPaymentType}
                onChange={(e) => setSelectedPaymentType(e.target.value)}
                disabled={isLoadingData}
              >
                {PAYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Stack>

          <Stack direction="horizontal" gap={3} className="align-items-end">
            <Form.Group controlId="statusSelect" className="flex-grow-1">
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Order Status
              </Form.Label>
              <Select
                isMulti
                name="statuses"
                options={ORDER_STATUS_OPTIONS}
                className="react-select-container"
                classNamePrefix="react-select"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                disabled={isLoadingData}
                placeholder="All Statuses"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '47.59px',
                    fontSize: '14px',
                    backgroundColor: '#fcfcfd',
                    borderWidth: '2px',
                    borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    },
                    boxShadow: state.isFocused
                      ? '0 0 0 0.25rem rgba(62, 139, 247, 0.25)'
                      : null,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '45.59px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#eef5ff',
                    borderRadius: '20px',
                    padding: '2px 8px',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    fontSize: '13px',
                    fontWeight: '500',
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    ':hover': {
                      backgroundColor: 'transparent',
                      color: '#3e8bf7',
                    },
                  }),
                }}
              />
            </Form.Group>

            {!isSeasonReport && (
              <Form.Group
                controlId="savedReportSelect"
                style={{ width: '240px' }}
              >
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Saved Report
                </Form.Label>
                <Form.Select
                  className="form-control-height"
                  value={selectedReportId}
                  onChange={(e) => applySavedReport(e.target.value)}
                  disabled={isLoadingData || isSavingReport}
                >
                  <option value={DEFAULT_SAVED_REPORT_VALUE}>
                    Default view
                  </option>
                  {savedReports.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                      {report.schedule?.enabled ? ' (scheduled)' : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <ReportRefreshButton
              onRefresh={refresh}
              isRefreshing={isLoadingData}
              disabled={isLoadingData || !fromDate || !toDate}
              testId="order-listings-refresh-button"
            />

            <Button
              variant="outline-light"
              className="icon-button form-control-height d-flex align-items-center gap-2 px-4"
              onClick={() => {
                setReportError('')
                setReportMessage('')
                if (selectedReport) {
                  setReportName(selectedReport.name || '')
                  setReportSchedule({
                    ...DEFAULT_SAVED_REPORT_SCHEDULE,
                    ...(selectedReport.schedule || {}),
                    email:
                      selectedReport.schedule?.email ||
                      AuthService.getUser()?.user?.email ||
                      '',
                  })
                }
                setShowSettings(true)
              }}
              style={{
                borderRadius: '20px',
                borderColor: '#e6e8ec',
                color: '#121923',
                fontWeight: '600',
              }}
            >
              <SettingsIcon />
              <span>Edit columns</span>
            </Button>

            <Button
              variant="outline-light"
              className="icon-button form-control-height d-flex align-items-center gap-2 px-4"
              disabled={
                isLoadingData ||
                !paymentListings ||
                paymentListings?.length === 0
              }
              onClick={downloadReport}
              style={{
                borderRadius: '20px',
                borderColor: '#e6e8ec',
                color: '#121923',
                fontWeight: '600',
              }}
            >
              <ExcelIcon />
              <span>Excel</span>
            </Button>
          </Stack>
        </Stack>
      </div>

      <Results
        isLoading={isLoadingData}
        fromDate={fromDate}
        toDate={toDate}
        data={enrichedListings}
        visibleColumns={visibleColumns}
        selectedSeasonLabel={selectedSeasonLabel}
        openOrderDetails={openOrderDetails}
      />

      <Modal
        show={showSettings}
        onHide={() => setShowSettings(false)}
        size="lg"
        scrollable
        className="column-settings-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Column Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="subtitle">
            Toggle columns on or off and rearrange their order in the report.
            The order is arranged from top to bottom, which is represented from
            left to right on the table.
          </p>
          {!isSeasonReport && (
            <div className="column-template-save mb-4">
              <Form.Group controlId="savedReportName">
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Report name
                </Form.Label>
                <Form.Control
                  type="text"
                  className="form-control-height"
                  value={reportName}
                  onChange={(e) => {
                    setReportName(e.target.value)
                    if (reportError) setReportError('')
                    if (reportMessage) setReportMessage('')
                  }}
                  placeholder="e.g. Weekly card sales"
                  maxLength={60}
                  disabled={isSavingReport || isSendingTestEmail}
                />
              </Form.Group>
              <p className="text-muted small mt-2 mb-0">
                Saves the current filters (payment type, source, product type,
                status, event, date filter) and column layout. Optionally email
                the matching report on a schedule.
              </p>
              <SavedReportScheduleFields
                schedule={reportSchedule}
                onChange={setReportSchedule}
                disabled={isSavingReport || isSendingTestEmail}
              />
              {reportError ? (
                <p className="text-danger small mt-2 mb-0">{reportError}</p>
              ) : null}
              {reportMessage ? (
                <p className="text-success small mt-2 mb-0">{reportMessage}</p>
              ) : null}
              <Stack direction="horizontal" gap={2} className="mt-3 flex-wrap">
                <Button
                  variant="outline-light"
                  className="btn-pill btn-cancel"
                  disabled={isSavingReport || isSendingTestEmail}
                  onClick={() => handleSaveReport({ updateExisting: false })}
                >
                  {isSavingReport ? 'Saving…' : 'Save as new report'}
                </Button>
                {selectedReport && (
                  <>
                    <Button
                      variant="primary"
                      className="btn-pill btn-save"
                      disabled={isSavingReport || isSendingTestEmail}
                      onClick={() => handleSaveReport({ updateExisting: true })}
                    >
                      Update report
                    </Button>
                    <Button
                      variant="outline-danger"
                      className="btn-pill"
                      disabled={isSavingReport || isSendingTestEmail}
                      onClick={handleDeleteReport}
                    >
                      Delete report
                    </Button>
                  </>
                )}
                <Button
                  variant="outline-light"
                  className="btn-pill btn-cancel"
                  disabled={isSavingReport || isSendingTestEmail}
                  onClick={handleSendTestReportEmail}
                >
                  {isSendingTestEmail ? 'Sending…' : 'Send test email'}
                </Button>
              </Stack>
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={0}>
                {columns.map((col, index) => (
                  <SortableColumnItem
                    key={col.id}
                    col={col}
                    index={index}
                    toggleColumn={toggleColumn}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-light"
            className="btn-pill btn-reset"
            onClick={resetColumns}
            disabled={isSavingReport || isSendingTestEmail}
          >
            <ResetIcon />
            Reset to default
          </Button>
          <Stack direction="horizontal" gap={2}>
            <Button
              variant="outline-light"
              className="btn-pill btn-cancel"
              onClick={() => setShowSettings(false)}
              disabled={isSavingReport || isSendingTestEmail}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-pill btn-save"
              onClick={() => setShowSettings(false)}
              disabled={isSavingReport || isSendingTestEmail}
            >
              Done
            </Button>
          </Stack>
        </Modal.Footer>
      </Modal>
    </section>
  )
}

const Listing = ({
  listing,
  visibleColumns,
  openOrderDetails,
  seasonLabel,
}) => {
  return (
    <tr className={getListingRowClassName(listing)}>
      {visibleColumns.map((col) => (
        <td key={col.id} className="text-left">
          {col.id === 'orderId' ? (
            <Button
              variant="link"
              className="p-0 text-link"
              onClick={() => openOrderDetails(listing.id, listing.orderId)}
            >
              {listing.orderId}
            </Button>
          ) : col.id === 'season' ? (
            listing?.season || seasonLabel || EMPTY_DISPLAY_VALUE
          ) : col.id === 'renewal' ? (
            formatRenewalForDisplay(listing)
          ) : col.id === 'pricingTier' ? (
            listing?.pricingTier || EMPTY_DISPLAY_VALUE
          ) : col.id === 'pricingTierPrice' ? (
            formatListingNumericCurrency(listing?.pricingTierPrice)
          ) : col.id === 'seasonTicketBaseRevenue' ? (
            formatListingNumericCurrency(getSeasonTicketBaseRevenue(listing))
          ) : col.id === 'createdAt' ? (
            (() => {
              const zonedDate = getTimezoneDate(
                listing.processedAt ?? listing.createdAt,
                listing.timezone
              )
              return zonedDate ? formatDateTime(zonedDate) : EMPTY_DISPLAY_VALUE
            })()
          ) : col.id === 'eventName' ? (
            getEventNameForOrder(listing)
          ) : col.id === 'seats' ? (
            formatSeatsForOrderListing(
              listing?.seatTickets,
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'eventStart' ? (
            getEventDateForOrder(listing, listing.timezone)
          ) : col.id === 'productType' ? (
            PRODUCT_TYPE_OPTIONS.find(
              (opt) => opt.value === listing.productType
            )?.label ||
            listing.productType ||
            EMPTY_DISPLAY_VALUE
          ) : RESALE_DETAIL_COLUMN_IDS.has(col.id) ? (
            formatResaleDetailValue(listing, col.id)
          ) : col.id === 'status' ? (
            (() => {
              const refundStatus = getOrderRefundStatusLabel(listing)
              const baseStatus =
                ORDER_STATUS_LABEL[listing.status] ||
                capitalizeString(
                  listing.status?.replace(/_/g, ' ') || EMPTY_DISPLAY_VALUE
                )
              if (isOrderListingRefundRow(listing)) {
                return refundStatus || baseStatus
              }
              return refundStatus === 'Partially Refunded'
                ? `${baseStatus} (${refundStatus})`
                : baseStatus
            })()
          ) : col.id === 'refundStatus' ? (
            formatListingRefundStatus(listing)
          ) : col.id === 'refundAmount' ? (
            formatListingNumericCurrency(
              isOrderListingRefundRow(listing)
                ? 0
                : getOrderRefundAmount(listing)
            )
          ) : col.id === 'lastRefundAt' ? (
            (() => {
              const lastRefundAt = getOrderLastRefundDate(listing)
              return lastRefundAt
                ? formatDateTime(moment(lastRefundAt))
                : EMPTY_DISPLAY_VALUE
            })()
          ) : col.id === 'paymentType' ? (
            PAYMENT_TYPE_LABEL[listing.paymentType] ||
            capitalizeString(listing.paymentType || EMPTY_DISPLAY_VALUE)
          ) : col.id === 'paymentPlan' ? (
            listing?.paymentPlan ? (
              'True'
            ) : (
              'False'
            )
          ) : col.id === 'paymentMethod' ? (
            formatPaymentMethodLabel(listing.paymentMethod)
          ) : col.id === 'customerEmail' ? (
            listing.customerEmail?.trim() || EMPTY_DISPLAY_VALUE
          ) : col.id === 'accountRep' ? (
            capitalizeString(listing.accountRep?.trim() || EMPTY_DISPLAY_VALUE)
          ) : col.id === 'invoiceNumber' ? (
            listing.invoiceNumber || EMPTY_DISPLAY_VALUE
          ) : col.id === 'organizationName' ? (
            listing.organizationName?.trim() || EMPTY_DISPLAY_VALUE
          ) : col.id === 'dateOfIssue' ? (
            listing.dateOfIssue ? (
              formatDateTime(moment(listing.dateOfIssue), 'dateOnly')
            ) : (
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'dueDate' ? (
            listing.dueDate ? (
              formatDateTime(moment(listing.dueDate), 'dateOnly')
            ) : (
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'datePaid' ? (
            listing.datePaid ? (
              formatDateTime(moment(listing.datePaid), 'dateOnly')
            ) : (
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'agingBucket' ? (
            listing.dueDate != null && listing.agingBucket ? (
              listing.agingBucket
            ) : (
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'source' ? (
            ORDER_SOURCE_LABEL[listing.source] ||
            capitalizeString(listing.source || EMPTY_DISPLAY_VALUE)
          ) : col.id === 'buyerType' ? (
            (listing.buyerType && String(listing.buyerType).trim()) ||
            EMPTY_DISPLAY_VALUE
          ) : col.id === 'last4' ? (
            listing.last4 ? (
              listing.last4.toString().padStart(4, '0')
            ) : (
              EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'customerName' ? (
            capitalizeString(
              listing.customerName?.trim() || EMPTY_DISPLAY_VALUE
            )
          ) : col.id === 'redemptionValue' ? (
            formatListingNumericCurrency(
              listing.voucher && listing.flex_pack ? listing.redemptionValue : 0
            )
          ) : col.id === 'amountRemaining' ? (
            formatListingNumericCurrency(listing?.amountRemaining)
          ) : col.id === 'discountCode' ? (
            (listing.discountCode && String(listing.discountCode).trim()) ||
            EMPTY_DISPLAY_VALUE
          ) : col.id === 'discountType' ? (
            DISCOUNT_TYPE_LABEL[listing.discountType] ||
            listing.discountType ||
            EMPTY_DISPLAY_VALUE
          ) : col.id === 'applyDiscountTo' ? (
            APPLY_DISCOUNT_TO_LABEL[listing.applyDiscountTo] ||
            listing.applyDiscountTo ||
            EMPTY_DISPLAY_VALUE
          ) : col.id === 'discountAmount' ? (
            formatDiscountAmountDisplay(listing)
          ) : col.id === 'totalDiscount' ? (
            formatListingNumericCurrency(listing.totalDiscount)
          ) : col.id === 'totalFeeAmount' ? (
            formatCurrency(
              toListingFeeDisplay(
                listing,
                getAdditionalFeesValueForListing(listing)
              )
            )
          ) : col.id === 'quantity' ? (
            formatListingNumericCount(listing.quantity)
          ) : col.isNumeric ? (
            col.id === 'serviceFee' ? (
              formatCurrency(
                toListingFeeDisplay(
                  listing,
                  getServiceFeeValueForListing(listing)
                )
              )
            ) : col.id === 'facilityFee' ? (
              formatCurrency(
                toListingFeeDisplay(
                  listing,
                  getFacilityFeeValueForListing(listing)
                )
              )
            ) : col.id === 'chargebackProtection' ? (
              formatCurrency(
                toListingFeeDisplay(
                  listing,
                  getChargebackValueForListing(listing)
                )
              )
            ) : col.id === 'processingFee' ? (
              formatCurrency(
                toListingFeeDisplay(
                  listing,
                  getProcessingFeeValueForListing(listing)
                )
              )
            ) : col.id === 'gross' ? (
              formatCurrency(getGrossValueForListing(listing))
            ) : col.id === 'net' ? (
              formatListingNumericCurrency(getNetValueForListing(listing))
            ) : (
              formatListingNumericCurrency(listing?.[col.id])
            )
          ) : (
            listing?.[col.id]
          )}
        </td>
      ))}
    </tr>
  )
}

const SummaryRow = ({ data, visibleColumns }) => {
  // `data` includes refund companion lines for full refunds and for partial
  // refunds that did not already reduce stored totals. Those negative lines
  // are what bring the footer down. Removals that already rewrote gross/net
  // stay as a single remaining-amount row.
  const totals = data.reduce((acc, item) => {
    visibleColumns.forEach((col) => {
      if (col.summable) {
        const valueToSum =
          col.id === 'net'
            ? getNetValueForListing(item)
            : col.id === 'gross'
              ? getGrossValueForListing(item)
              : col.id === 'serviceFee'
                ? getServiceFeeValueForListing(item)
                : col.id === 'facilityFee'
                  ? getFacilityFeeValueForListing(item)
                  : col.id === 'processingFee'
                    ? getProcessingFeeValueForListing(item)
                    : col.id === 'chargebackProtection'
                      ? getChargebackValueForListing(item)
                      : col.id === 'totalFeeAmount'
                        ? getAdditionalFeesValueForListing(item)
                        : col.id === 'seasonTicketBaseRevenue'
                          ? getSeasonTicketBaseRevenue(item)
                          : item[col.id]
        acc[col.id] = (acc[col.id] || 0) + parseFloat(valueToSum || 0)
      }
    })
    return acc
  }, {})

  const firstNonSummableIndex = visibleColumns.findIndex((col) => !col.summable)

  return (
    <tr className="md-x md-x--scroll total-row light fw-bold">
      {visibleColumns.map((col, index) => (
        <td key={col.id} className="text-left uppercase text-body-xs">
          {col.summable
            ? col.id === 'serviceFee' ||
              col.id === 'facilityFee' ||
              col.id === 'processingFee' ||
              col.id === 'chargebackProtection' ||
              col.id === 'totalFeeAmount'
              ? formatCurrency(toNegativeFeeDisplay(totals[col.id]))
              : formatCurrency(totals[col.id])
            : index === firstNonSummableIndex
              ? 'TOTAL'
              : ''}
        </td>
      ))}
    </tr>
  )
}
const TableHeader = ({ visibleColumns }) => (
  <thead>
    <tr className="md md--scroll">
      {visibleColumns.map((col) => (
        <th key={col.id} className="text-left uppercase text-body">
          {humanizeHeader(col.label)}
        </th>
      ))}
    </tr>
  </thead>
)

function Results({
  isLoading,
  data,
  fromDate,
  toDate,
  visibleColumns,
  selectedSeasonLabel,
  openOrderDetails,
}) {
  if (!fromDate || !toDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select a date range from the options above. Once you've made your
            selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for the specified criteria. Please try
            different filters.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md mb-4">
      <div className="order-listings-legend">
        <span className="order-listings-legend__item">
          <span className="order-listings-legend__swatch order-listings-legend__swatch--resale" />
          Resale
        </span>
        <span className="order-listings-legend__item">
          <span className="order-listings-legend__swatch order-listings-legend__swatch--refund" />
          Refund
        </span>
      </div>
      <div className="table-container">
        <div className="table-container--fixed-height--scrollable">
          <Table responsive hover>
            <TableHeader visibleColumns={visibleColumns} />
            <tbody>
              {data.map((order, idx) => (
                <Listing
                  key={`${order.id ?? order.orderId ?? 'row'}-${
                    isOrderListingRefundRow(order) ? 'refund' : 'sale'
                  }-${idx}`}
                  listing={order}
                  visibleColumns={visibleColumns}
                  seasonLabel={selectedSeasonLabel}
                  openOrderDetails={openOrderDetails}
                />
              ))}
              <SummaryRow data={data} visibleColumns={visibleColumns} />
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}
